/**
 * IdentityTab — Tab 0 of the Item form.
 *
 * Flat continuous form — no inner cards.
 * Sections are separated by a title + divider only.
 * Layout: full-width two-column grid throughout.
 *
 * Classification is selected top-down: Material Group → Item Class → Category.
 * Medicine search / details appear only when the selected group is Pharmacy.
 *
 * Props:
 *   control           RHF control
 *   errors            RHF errors
 *   setValue          RHF setValue fn
 *   onGuardFocus      (fieldName?) => void — jump to first empty required field
 *   materialGroups    [{ value, label }]
 *   itemClasses       [{ value, label }] — already filtered to the selected group
 *   categories        [{ value, label }] — already filtered to the selected class
 *   uoms              [{ value, label }]
 *   classDisabled     bool — no material group selected yet
 *   categoryDisabled  bool — no item class selected yet
 *   classHelperText   string
 *   categoryHelperText string
 *   isPharmacy        bool — selected material group is Pharmacy
 */
import React from 'react';
import {
  Grid, Box, Divider, Typography,
  Switch, FormControlLabel, Alert,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ModuleBadge from '../ModuleBadge';
import { InvTextField, InvSelect } from '../InvField';
import MedicineNameAutocomplete from './MedicineNameAutocomplete';
import { INV_COLORS } from '../../theme';

function SectionTitle({ label, first = false }) {
  return (
    <Box sx={{ mt: first ? 0 : 3, mb: 2 }}>
      <Typography sx={{
        fontSize: 15,
        fontWeight: 600,
        color: INV_COLORS.text,
        mb: 1,
      }}>
        {label}
      </Typography>
      <Divider sx={{ borderColor: INV_COLORS.borderLight }} />
    </Box>
  );
}

export default function IdentityTab({
  control, errors, setValue, onGuardFocus = () => {},
  materialGroups, itemClasses, categories, uoms, uomHelperText,
  classDisabled = false, categoryDisabled = false,
  classHelperText, categoryHelperText,
  isPharmacy = false,
}) {
  function clearMedicineFields() {
    setValue('generic_name', '', { shouldDirty: true });
    setValue('manufacturer_name', '', { shouldDirty: true });
  }

  return (
    <Box>

      {/* ── Basic Information ─────────────────────────────────── */}
      <SectionTitle label="Basic Information" first />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="material_group_id"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }) => (
              <InvSelect
                label="Material Group *"
                name={field.name}
                value={field.value}
                inputRef={field.ref}
                onFocus={() => onGuardFocus('material_group_id')}
                onBlur={field.onBlur}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('item_class_id', '', { shouldDirty: true });
                  setValue('category_id', '', { shouldDirty: true });
                  clearMedicineFields();
                }}
                options={materialGroups}
                error={errors.material_group_id}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="item_class_id"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }) => (
              <InvSelect
                label="Item Class *"
                name={field.name}
                value={field.value}
                inputRef={field.ref}
                onFocus={() => onGuardFocus('item_class_id')}
                onBlur={field.onBlur}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('category_id', '', { shouldDirty: true });
                }}
                options={itemClasses}
                error={errors.item_class_id}
                disabled={classDisabled}
                helperText={classHelperText}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="category_id"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }) => (
              <InvSelect
                label="Category *"
                name={field.name}
                value={field.value}
                inputRef={field.ref}
                onFocus={() => onGuardFocus('category_id')}
                onBlur={field.onBlur}
                onChange={field.onChange}
                options={categories}
                error={errors.category_id}
                disabled={categoryDisabled}
                helperText={categoryHelperText}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="base_uom_id"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }) => (
              <InvSelect
                label="Base UOM *"
                name={field.name}
                value={field.value}
                inputRef={field.ref}
                onFocus={() => onGuardFocus('base_uom_id')}
                onBlur={field.onBlur}
                onChange={field.onChange}
                options={uoms}
                error={errors.base_uom_id}
                helperText={uomHelperText}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="item_name"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }) => (
              isPharmacy ? (
                <MedicineNameAutocomplete
                  value={field.value}
                  error={errors.item_name}
                  inputRef={field.ref}
                  onFocus={() => onGuardFocus('item_name')}
                  onInputChange={(text) => {
                    field.onChange(text);
                    clearMedicineFields();
                  }}
                  onSelect={(med) => {
                    field.onChange(med.term || '');
                    setValue('generic_name', med.generic_name || '', { shouldDirty: true });
                    setValue('manufacturer_name', med.manufacturer_name || '', { shouldDirty: true });
                  }}
                />
              ) : (
                <InvTextField
                  {...field}
                  inputRef={field.ref}
                  label="Item Name *"
                  error={errors.item_name}
                  onFocus={() => onGuardFocus('item_name')}
                />
              )
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="item_short_name"
            control={control}
            render={({ field }) => (
              <InvTextField
                {...field}
                label="Short Name"
                onFocus={() => onGuardFocus()}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* ── Medicine Details (Pharmacy group only) ────────────── */}
      {isPharmacy && (
        <>
          <SectionTitle label="Medicine Details" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="generic_name"
                control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Generic Name" onFocus={() => onGuardFocus()} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="brand_name"
                control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Brand Name" onFocus={() => onGuardFocus()} />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="manufacturer_name"
                control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Manufacturer" onFocus={() => onGuardFocus()} />
                )}
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* ── Remarks ───────────────────────────────────────────── */}
      <SectionTitle label="Remarks" />
      <Controller
        name="remarks"
        control={control}
        render={({ field }) => (
          <InvTextField {...field} label="Remarks" multiline rows={3} onFocus={() => onGuardFocus()} />
        )}
      />

      {/* ── Additional Information ────────────────────────────── */}
      <SectionTitle label="Additional Information" />
      <Grid container spacing={2} alignItems="flex-start">

        <Grid item xs={12} sm={6}>
          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!field.value}
                      onChange={e => field.onChange(e.target.checked)}
                      color="success"
                    />
                  }
                  label="Item is active"
                  sx={{ color: INV_COLORS.text, display: 'flex', m: 0 }}
                />
                {!field.value && (
                  <Alert
                    severity="warning"
                    icon={<WarningAmberIcon />}
                    sx={{
                      mt: 1.5,
                      backgroundColor: `${INV_COLORS.amber}15`,
                      color: INV_COLORS.amber,
                      border: `1px solid ${INV_COLORS.amber}44`,
                      '& .MuiAlert-icon': { color: INV_COLORS.amber },
                      fontSize: 13,
                    }}
                  >
                    Deactivating this item will prevent it from being used in new transactions.
                  </Alert>
                )}
              </>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 0.75 }}>
            <Typography sx={{ fontSize: 13, color: INV_COLORS.textMuted, fontWeight: 500 }}>
              Module
            </Typography>
            <ModuleBadge isPharmacy={isPharmacy} />
          </Box>
        </Grid>

      </Grid>
    </Box>
  );
}
